import { Col, Row, Space } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ApplicationStage } from '../../../../api/types';
import BasicProLayoutContainer from '../../../../layouts/BasicProLayout/BasicProLayoutContainer';
import { useThemeComponents } from '../../../../themes';
import { getApplicationIdentifier } from '../../../../utilities/application';
import FullJourneySteps from '../../../shared/JourneyPage/FullJourneySteps';
import BookingDeposit from '../../../shared/ThankYou/BookingDeposit';
import DealerInfo from '../../../shared/ThankYou/DealerInfo';
import SubmittedContent from '../../../shared/ThankYou/SubmittedContent';
import VehicleDetails from '../../../shared/ThankYou/VehicleDetails';
import { usePersistStandardJourneyValues } from '../CarDetailsPage/usePersistStandardJourneyValues';
import { type StandardApplicationState } from '../Journey/JourneyController';
import { type State } from '../Journey/reducer';

const colSpan = { lg: 8, md: 12, sm: 24, xs: 24 };

interface ContentProps {
    state: State<StandardApplicationState>;
}

const Content = ({ state }: ContentProps) => {
    const { t } = useTranslation('standardApplicationThankYou');
    const { StandardLayout, Button } = useThemeComponents();
    const { application } = state;
    const hasDeposit = !!application.deposit;

    const navigate = useNavigate();
    const { remove } = usePersistStandardJourneyValues();
    useEffect(() => {
        // remove persisted values from session storage
        remove();
    }, [remove]);

    const goBackToJourney = () => {
        navigate('..');
    };

    const identifier = useMemo(
        () =>
            getApplicationIdentifier(application, [
                ApplicationStage.Financing,
                ApplicationStage.Reservation,
                ApplicationStage.Lead,
            ]),
        [application]
    );

    return (
        <StandardLayout title={t('standardApplicationThankYou:title')}>
            <BasicProLayoutContainer>
                <FullJourneySteps stages={state?.stages} />
                <Row gutter={[24, 24]} justify="center" style={{ marginBottom: '24px' }}>
                    <Col {...colSpan}>
                        <Space direction="vertical" size={24} style={{ width: '100%' }}>
                            <VehicleDetails state={state} />
                            {hasDeposit && <BookingDeposit depositAmount={application.deposit.amount} />}
                        </Space>
                    </Col>
                    <Col {...colSpan}>
                        <Space direction="vertical" size={32} style={{ width: '100%' }}>
                            <SubmittedContent
                                description={t('standardApplicationThankYou:contents.description.emailSendMessage')}
                                reference={t('standardApplicationThankYou:contents.reference', {
                                    reference: identifier,
                                })}
                                title={t('standardApplicationThankYou:contents.description.successMessage')}
                            />
                            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                                {application?.dealer && <DealerInfo dealer={application?.dealer} />}
                                <Button
                                    key="done"
                                    data-cy="eventThankPageButton"
                                    htmlType="button"
                                    onClick={goBackToJourney}
                                    type="primary"
                                    block
                                >
                                    {t('standardApplicationThankYou:actions.continueBrowsing')}
                                </Button>
                            </Space>
                        </Space>
                    </Col>
                </Row>
            </BasicProLayoutContainer>
        </StandardLayout>
    );
};

export default Content;
